/**
 * Portals the passed node to a different part of the document. Note that portal does not transfer shadow scoped styles, unlike `es-popover`, so any portaled elements should be self contained.
 */
export class Portal {
    constructor() {
        this.bubbleRequestClose = (e) => {
            this.requestClose.emit(e);
        };
        this.target = 'body';
        this.renderElement = undefined;
        this.open = false;
        this.backdrop = false;
        this.preventOverscroll = false;
    }
    connectedCallback() {
        if (!this.open)
            return;
        this.portalledBackdrop?.cancelClose();
        this.attachElement();
    }
    disconnectedCallback() {
        if (!this.open)
            return;
        this.detatchElement();
    }
    /** @internal */
    async attachElement() {
        const target = document.querySelector(this.target);
        if (!target)
            return;
        if (!this.portalledBackdrop) {
            const backdrop = document.createElement('es-backdrop');
            backdrop.showBackdrop = this.backdrop;
            backdrop.preventOverscroll = this.preventOverscroll;
            const zIndexBase = getComputedStyle(this.host).getPropertyValue('--zindex-base');
            backdrop.style.setProperty('--zindex-base', zIndexBase);
            backdrop.style.opacity = '0';
            target.appendChild(backdrop);
            backdrop.addEventListener('requestClose', this.bubbleRequestClose);
            this.portalledBackdrop = backdrop;
        }
        this.portalledBackdrop.renderNode(this.renderElement);
    }
    /** @internal */
    async detatchElement() {
        if (!this.portalledBackdrop)
            return;
        const backdrop = this.portalledBackdrop;
        if (await backdrop.close()) {
            backdrop.parentNode?.removeChild(backdrop);
            this.portalledBackdrop = undefined;
        }
    }
    render() {
        if (this.open) {
            this.attachElement();
        }
        else {
            this.detatchElement();
        }
    }
    static get is() { return "es-portal"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["es-portal.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["es-portal.css"]
        };
    }
    static get properties() {
        return {
            "target": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A query selector to select the location to portal to."
                },
                "attribute": "target",
                "reflect": false,
                "defaultValue": "'body'"
            },
            "renderElement": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "RenderFunction",
                    "resolved": "(h: typeof h) => string | VNode | VNode[] | null",
                    "references": {
                        "RenderFunction": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/types.ts::RenderFunction"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The element to render."
                }
            },
            "open": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If the element is portaled or not."
                },
                "attribute": "open",
                "reflect": true,
                "defaultValue": "false"
            },
            "backdrop": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If the portal should overlay a backdrop, to prevent external clicks."
                },
                "attribute": "backdrop",
                "reflect": false,
                "defaultValue": "false"
            },
            "preventOverscroll": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If the portal should prevent overscroll"
                },
                "attribute": "prevent-overscroll",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "requestClose",
                "name": "requestClose",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Triggers when the popover requests to close."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "attachElement": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": [{
                            "name": "internal",
                            "text": undefined
                        }]
                }
            },
            "detatchElement": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": [{
                            "name": "internal",
                            "text": undefined
                        }]
                }
            }
        };
    }
    static get elementRef() { return "host"; }
}
//# sourceMappingURL=es-portal.js.map
